import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, BookOpen, Calendar, DollarSign, 
  TrendingUp, Award, Loader2, ArrowUpRight, Package, AlertTriangle, Mail, Database
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [inventoryAlerts, setInventoryAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingAlerts, setSendingAlerts] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchInventoryAlerts();
  }, []);

  const fetchInventoryAlerts = async () => {
    try {
      const response = await api.get('/kits/inventory_alerts/');
      setInventoryAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory alerts:', error);
      setInventoryAlerts({ alerts: [], summary: { out_of_stock: 0, low_stock: 0, total_alerts: 0 } });
    }
  };

  const handleSendAlertEmails = async () => {
    setSendingAlerts(true);
    try {
      const response = await api.post('/kits/send_stock_alerts/');
      if (response.data.emails_sent > 0) {
        toast.success(`Stock alerts sent to ${response.data.emails_sent} admin(s)`);
      } else {
        toast.info(response.data.message || 'No alerts to send');
      }
    } catch (error) {
      console.error('Failed to send alerts:', error);
      toast.error('Failed to send alert emails');
    } finally {
      setSendingAlerts(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [revenueRes, enrollmentRes, attendanceRes, gamificationRes] = await Promise.all([
        api.get('/reports/revenue'),
        api.get('/reports/enrollment'),
        api.get('/reports/attendance'),
        api.get('/reports/gamification'),
      ]);
      setStats({
        revenue: revenueRes.data,
        enrollment: enrollmentRes.data,
        attendance: attendanceRes.data,
        gamification: gamificationRes.data,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Demo data
      setStats({
        revenue: { total_revenue: 15420, transaction_count: 45 },
        enrollment: { total_enrollments: 89, confirmed: 75, waitlisted: 8, cancelled: 6 },
        attendance: { attendance_rate: 92.5, total_records: 450 },
        gamification: { total_points_earned: 12500, total_redemptions: 23, total_raffle_entries: 156 },
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Total Revenue', 
      value: `$${stats?.revenue?.total_revenue?.toLocaleString() || 0}`, 
      icon: DollarSign, 
      color: 'bg-accent',
      subtext: `${stats?.revenue?.transaction_count || 0} transactions`
    },
    { 
      label: 'Active Enrollments', 
      value: stats?.enrollment?.confirmed || 0, 
      icon: Users, 
      color: 'bg-primary',
      subtext: `${stats?.enrollment?.waitlisted || 0} waitlisted`
    },
    { 
      label: 'Attendance Rate', 
      value: `${stats?.attendance?.attendance_rate?.toFixed(1) || 0}%`, 
      icon: Calendar, 
      color: 'bg-secondary',
      subtext: `${stats?.attendance?.total_records || 0} records`
    },
    { 
      label: 'Points Earned', 
      value: stats?.gamification?.total_points_earned?.toLocaleString() || 0, 
      icon: Award, 
      color: 'bg-blue-500',
      subtext: `${stats?.gamification?.total_redemptions || 0} redemptions`
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">Overview of your coding school operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="card-dashboard" data-testid={`stat-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-accent" />
                </div>
                <p className="text-3xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Enrollment Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'Confirmed', value: stats?.enrollment?.confirmed || 0, color: 'bg-accent' },
                { label: 'Waitlisted', value: stats?.enrollment?.waitlisted || 0, color: 'bg-blue-500' },
                { label: 'Cancelled', value: stats?.enrollment?.cancelled || 0, color: 'bg-destructive' },
                { label: 'Completed', value: stats?.enrollment?.completed || 0, color: 'bg-muted-foreground' },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <span>{item.label}</span>
                  </div>
                  <span className="font-bold">{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Gamification Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span>Total Points Earned</span>
                <span className="font-bold text-secondary">
                  {stats?.gamification?.total_points_earned?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Total Points Spent</span>
                <span className="font-bold">
                  {stats?.gamification?.total_points_spent?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Reward Redemptions</span>
                <span className="font-bold">{stats?.gamification?.total_redemptions || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Raffle Entries</span>
                <span className="font-bold">{stats?.gamification?.total_raffle_entries || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Alerts Section */}
      {inventoryAlerts && inventoryAlerts.summary?.total_alerts > 0 && (
        <Card className="card-dashboard border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Kit Inventory Alerts
              <Badge variant="destructive" className="ml-2">
                {inventoryAlerts.summary.total_alerts}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inventoryAlerts.summary.out_of_stock > 0 && (
                <div className="flex items-center gap-2 text-destructive">
                  <Package className="h-4 w-4" />
                  <span className="font-medium">{inventoryAlerts.summary.out_of_stock} kit(s) OUT OF STOCK</span>
                </div>
              )}
              {inventoryAlerts.summary.low_stock > 0 && (
                <div className="flex items-center gap-2 text-amber-600">
                  <Package className="h-4 w-4" />
                  <span className="font-medium">{inventoryAlerts.summary.low_stock} kit(s) running low</span>
                </div>
              )}
              
              {inventoryAlerts.alerts.length > 0 && (
                <div className="mt-4 space-y-2">
                  {inventoryAlerts.alerts.slice(0, 5).map(kit => (
                    <div key={kit.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <span className="font-medium">{kit.name}</span>
                        {kit.sku && <span className="text-xs text-muted-foreground ml-2">({kit.sku})</span>}
                      </div>
                      <Badge variant={kit.stock_quantity === 0 ? 'destructive' : 'secondary'}>
                        {kit.stock_quantity} / {kit.reorder_threshold} threshold
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                <Link to="/admin/kits">
                  <Button variant="outline" size="sm">
                    <Package className="h-4 w-4 mr-2" />
                    Manage Inventory
                  </Button>
                </Link>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handleSendAlertEmails}
                  disabled={sendingAlerts}
                >
                  {sendingAlerts ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send Alert Emails
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
